import { calculateItemTotal } from "@sellar-io/common";
import { BasketOrderItem } from "../../../../../hooks";
import type { PromotionListResponseType } from "@sellar-io/api-v2/types";
import { getPercentageSpendRequirementMet } from "./getPercentageSpendRequirementMet";

type CanPromotionRewardItemBeClaimedMessageType =
  | "MISSING_PROMOTION"
  | "MISSING_PROMOTION_REWARD_ITEM"
  | "REWARD_RULE_MET"
  | "SPEND_THRSHOLD_NOT_MET"
  | "PROMOTION_REWARD_ITEM_ALREADY_CLAIMED"
  | "UNKNOWN_ERROR";

type SuccessResponse = {
  canBeClaimed: true;
  message: null;
};

type ErrorResponse = {
  canBeClaimed: false;
  message: CanPromotionRewardItemBeClaimedMessageType;
};

type GetCanPromotionRewardItemBeClaimedParams = {
  promotionRewardItemId: number;
  nonPromotionBasketOrderItems: BasketOrderItem[];
  claimedPromotionRewardItemIds: number[];
  promotions: PromotionListResponseType;
};

type GetCanPromotionRewardItemBeClaimedResponse =
  | SuccessResponse
  | ErrorResponse;

export const getCanPromotionRewardItemBeClaimed = ({
  promotionRewardItemId,
  nonPromotionBasketOrderItems,
  claimedPromotionRewardItemIds,
  promotions,
}: GetCanPromotionRewardItemBeClaimedParams): GetCanPromotionRewardItemBeClaimedResponse => {
  const basketSubtotal = calculateItemTotal(nonPromotionBasketOrderItems);

  const promotion = promotions.find((promotion) => {
    return promotion.PromotionRewardItems.some(
      (promotionRewardItem) => promotionRewardItem.id === promotionRewardItemId,
    );
  });

  if (!promotion) {
    return {
      canBeClaimed: false,
      message: "MISSING_PROMOTION",
    };
  }

  const promotionRewardItem = promotion.PromotionRewardItems.find(
    (promotionRewardItem) => promotionRewardItem.id === promotionRewardItemId,
  );

  if (!promotionRewardItem) {
    return {
      canBeClaimed: false,
      message: "MISSING_PROMOTION_REWARD_ITEM",
    };
  }

  const percentageSpendRequirementMet = getPercentageSpendRequirementMet({
    spendThreshold: promotionRewardItem.spendThreshold,
    basketSubtotal,
  });

  const hasPromotionRewardItemBeenClaimed =
    claimedPromotionRewardItemIds.includes(promotionRewardItem.id);

  const hasPromotionRewardRuleBeenMet = () => {
    switch (promotion.rewardRule) {
      case "all_of":
        return false;
      case "one_of": {
        const promotionRewardItems = promotion.PromotionRewardItems;
        const hasMetRewardRule = promotionRewardItems.some(
          (promotionRewardItem) => {
            return claimedPromotionRewardItemIds.includes(
              promotionRewardItem.id,
            );
          },
        );
        return hasMetRewardRule;
      }
      default:
        return false;
    }
  };

  if (percentageSpendRequirementMet !== 100) {
    return {
      canBeClaimed: false,
      message: "SPEND_THRSHOLD_NOT_MET",
    };
  }

  if (hasPromotionRewardItemBeenClaimed) {
    return {
      canBeClaimed: false,
      message: "PROMOTION_REWARD_ITEM_ALREADY_CLAIMED",
    };
  }

  const hasRewardRuleBeenMet = hasPromotionRewardRuleBeenMet();

  if (hasRewardRuleBeenMet) {
    return {
      canBeClaimed: false,
      message: "REWARD_RULE_MET",
    };
  }

  return {
    canBeClaimed: true,
    message: null,
  };
};
